import type { EolStyle } from "../doc/eol.js";
import { normalizeRows, parseCsv, serializeCsv } from "./csvModel.js";

export type CsvGridCallbacks = {
  getEol: () => EolStyle;
  onCommit: (text: string) => void;
};

export type CsvGridEditorApi = {
  destroy(): void;
  syncFromText(text: string): void;
};

function columnLabel(index: number): string {
  let n = Math.max(0, index);
  let out = "";
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

function buildToolbar(opsHost: HTMLElement, on: (action: string) => void): void {
  opsHost.className = "npp-csv-ops";
  const mk = (label: string, action: string, title: string): HTMLButtonElement => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "npp-csv-toolbar-btn";
    b.textContent = label;
    b.title = title;
    b.addEventListener("click", () => on(action));
    return b;
  };
  opsHost.append(
    mk("+ row", "row-end", "Append row at bottom"),
    mk("↑ row", "row-ins-above", "Insert row above focused cell"),
    mk("↓ row", "row-ins-below", "Insert row below focused cell"),
    mk("− row", "row-del", "Delete focused row"),
    mk("+ col", "col-end", "Append column at right"),
    mk("← col", "col-ins-left", "Insert column left of focused cell"),
    mk("→ col", "col-ins-right", "Insert column right of focused cell"),
    mk("− col", "col-del", "Delete focused column"),
  );
}

export function createCsvGridEditor(
  tableHost: HTMLElement,
  opsHost: HTMLElement,
  cbs: CsvGridCallbacks,
): CsvGridEditorApi {
  let rows: string[][] = [[""]];
  let focusR = 0;
  let focusC = 0;
  let destroyed = false;
  let rangeStartR = 0;
  let rangeStartC = 0;
  let rangeEndR = 0;
  let rangeEndC = 0;
  let contextMenuEl: HTMLDivElement | null = null;
  let contextMenuCleanup: (() => void) | null = null;

  const commit = (): void => {
    if (destroyed) return;
    cbs.onCommit(serializeCsv(rows, cbs.getEol()));
  };

  const ensureShape = (): void => {
    rows = normalizeRows(rows);
    if (!rows.length) rows = [[""]];
  };

  const resetRangeToFocus = (): void => {
    rangeStartR = focusR;
    rangeStartC = focusC;
    rangeEndR = focusR;
    rangeEndC = focusC;
  };

  const selectedBounds = (): { r0: number; r1: number; c0: number; c1: number } => ({
    r0: Math.min(rangeStartR, rangeEndR),
    r1: Math.max(rangeStartR, rangeEndR),
    c0: Math.min(rangeStartC, rangeEndC),
    c1: Math.max(rangeStartC, rangeEndC),
  });

  const isInSelection = (r: number, c: number): boolean => {
    const b = selectedBounds();
    return r >= b.r0 && r <= b.r1 && c >= b.c0 && c <= b.c1;
  };

  const selectionToTsv = (): string => {
    const b = selectedBounds();
    const lines: string[] = [];
    for (let r = b.r0; r <= b.r1; r++) {
      const cells: string[] = [];
      for (let c = b.c0; c <= b.c1; c++) {
        cells.push(rows[r]?.[c] ?? "");
      }
      lines.push(cells.join("\t"));
    }
    return lines.join("\n");
  };

  const ensureAtLeastSize = (minRows: number, minCols: number): void => {
    ensureShape();
    while (rows.length < minRows) {
      rows.push(Array(rows[0]!.length).fill(""));
    }
    if (rows[0]!.length < minCols) {
      for (const row of rows) {
        while (row.length < minCols) row.push("");
      }
    }
    ensureShape();
  };

  const parseTsvGrid = (text: string): string[][] => {
    const normalized = text.replace(/\r\n?/g, "\n");
    const lines = normalized.split("\n");
    if (lines.length && lines[lines.length - 1] === "") lines.pop();
    if (!lines.length) return [[""]];
    return lines.map((line) => line.split("\t"));
  };

  const clearSelectionCells = (): void => {
    const b = selectedBounds();
    for (let r = b.r0; r <= b.r1; r++) {
      for (let c = b.c0; c <= b.c1; c++) {
        rows[r]![c] = "";
      }
    }
    commit();
    render();
    focusCellAt(focusR, focusC);
  };

  const copySelectionToClipboard = async (): Promise<void> => {
    const text = selectionToTsv();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Ignore clipboard permission errors; keyboard copy still works when supported.
    }
  };

  const pasteFromClipboardToSelection = async (): Promise<void> => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      pasteGridAtSelection(text);
    } catch {
      // Ignore clipboard permission errors.
    }
  };

  const closeContextMenu = (): void => {
    contextMenuCleanup?.();
    contextMenuCleanup = null;
    if (contextMenuEl) {
      contextMenuEl.remove();
      contextMenuEl = null;
    }
  };

  const openContextMenu = (
    clientX: number,
    clientY: number,
    entries: Array<{ label: string; action?: () => void | Promise<void>; separator?: boolean }>,
  ): void => {
    closeContextMenu();
    const menu = document.createElement("div");
    menu.className = "npp-csv-context-menu";
    menu.setAttribute("role", "menu");
    entries.forEach((entry) => {
      if (entry.separator) {
        const sep = document.createElement("div");
        sep.className = "npp-csv-context-menu-separator";
        menu.appendChild(sep);
        return;
      }
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "npp-csv-context-menu-item";
      btn.textContent = entry.label;
      btn.addEventListener("click", () => {
        closeContextMenu();
        void entry.action?.();
      });
      menu.appendChild(btn);
    });
    document.body.appendChild(menu);
    const w = menu.offsetWidth;
    const h = menu.offsetHeight;
    const vpW = window.innerWidth;
    const vpH = window.innerHeight;
    const left = Math.max(0, clientX + w > vpW ? vpW - w : clientX);
    const yBelow = clientY;
    const yAbove = clientY - h;
    const top = yBelow + h > vpH ? (yAbove >= 0 ? yAbove : Math.max(0, vpH - h)) : yBelow;
    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
    const onPointerDown = (ev: MouseEvent): void => {
      if (!menu.contains(ev.target as Node)) closeContextMenu();
    };
    const onKeyDown = (ev: KeyboardEvent): void => {
      if (ev.key === "Escape") closeContextMenu();
    };
    const onWindowChange = (): void => closeContextMenu();
    window.addEventListener("mousedown", onPointerDown, true);
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("resize", onWindowChange, true);
    window.addEventListener("scroll", onWindowChange, true);
    contextMenuCleanup = () => {
      window.removeEventListener("mousedown", onPointerDown, true);
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("resize", onWindowChange, true);
      window.removeEventListener("scroll", onWindowChange, true);
    };
    contextMenuEl = menu;
  };

  const pasteGridAtSelection = (clipText: string): void => {
    const incoming = parseTsvGrid(clipText);
    if (!incoming.length) return;
    const startR = Math.min(rangeStartR, rangeEndR);
    const startC = Math.min(rangeStartC, rangeEndC);
    const h = incoming.length;
    const w = Math.max(1, ...incoming.map((r) => r.length));
    ensureAtLeastSize(startR + h, startC + w);
    for (let r = 0; r < h; r++) {
      for (let c = 0; c < w; c++) {
        rows[startR + r]![startC + c] = incoming[r]?.[c] ?? "";
      }
    }
    focusR = startR;
    focusC = startC;
    rangeStartR = startR;
    rangeStartC = startC;
    rangeEndR = startR + h - 1;
    rangeEndC = startC + w - 1;
    commit();
    render();
    focusCellAt(focusR, focusC);
  };

  const focusCellAt = (r: number, c: number): void => {
    const rr = Math.max(0, Math.min(rows.length - 1, r));
    const cc = Math.max(0, Math.min(rows[0]!.length - 1, c));
    const el = tableHost.querySelector<HTMLInputElement>(`input.npp-csv-cell[data-r="${rr}"][data-c="${cc}"]`);
    if (el) el.focus();
  };

  const render = (): void => {
    closeContextMenu();
    ensureShape();
    focusR = Math.max(0, Math.min(rows.length - 1, focusR));
    focusC = Math.max(0, Math.min(rows[0]!.length - 1, focusC));
    rangeStartR = Math.max(0, Math.min(rows.length - 1, rangeStartR));
    rangeEndR = Math.max(0, Math.min(rows.length - 1, rangeEndR));
    rangeStartC = Math.max(0, Math.min(rows[0]!.length - 1, rangeStartC));
    rangeEndC = Math.max(0, Math.min(rows[0]!.length - 1, rangeEndC));
    tableHost.replaceChildren();
    const wrap = document.createElement("div");
    wrap.className = "npp-csv-table-scroll";
    const table = document.createElement("table");
    table.className = "npp-csv-table";
    let draggingSelection = false;
    const thead = document.createElement("thead");
    const headRow = document.createElement("tr");
    const corner = document.createElement("th");
    corner.className = "npp-csv-col-header npp-csv-col-header--corner";
    corner.setAttribute("aria-hidden", "true");
    headRow.appendChild(corner);
    for (let c = 0; c < rows[0]!.length; c++) {
      const th = document.createElement("th");
      th.className = "npp-csv-col-header";
      th.scope = "col";
      th.textContent = columnLabel(c);
      if (c === focusC) th.classList.add("npp-csv-col-header--active");
      headRow.appendChild(th);
    }
    thead.appendChild(headRow);
    table.appendChild(thead);
    let activeCellHost: HTMLTableCellElement | null = null;
    table.addEventListener("focusin", (ev) => {
      const t = ev.target;
      if (!(t instanceof HTMLInputElement)) return;
      const r = t.dataset.r;
      const c = t.dataset.c;
      if (r === undefined || c === undefined) return;
      focusR = Math.max(0, Math.min(rows.length - 1, +r));
      focusC = Math.max(0, Math.min(rows[0]!.length - 1, +c));
      resetRangeToFocus();
      activeCellHost?.classList.remove("npp-csv-cell-host--active");
      activeCellHost = t.closest("td");
      activeCellHost?.classList.add("npp-csv-cell-host--active");
    });
    const tbody = document.createElement("tbody");
    for (let r = 0; r < rows.length; r++) {
      const tr = document.createElement("tr");
      const rh = document.createElement("th");
      rh.className = "npp-csv-row-header";
      rh.scope = "row";
      rh.textContent = String(r + 1);
      if (r === focusR) rh.classList.add("npp-csv-row-header--active");
      tr.appendChild(rh);
      for (let c = 0; c < rows[r]!.length; c++) {
        const td = document.createElement("td");
        td.className = "npp-csv-cell-host";
        if (isInSelection(r, c)) td.classList.add("npp-csv-cell-host--selected");
        if (r === focusR && c === focusC) td.classList.add("npp-csv-cell-host--active");
        const inp = document.createElement("input");
        inp.type = "text";
        inp.className = "npp-csv-cell";
        inp.value = rows[r]![c] ?? "";
        inp.dataset.r = String(r);
        inp.dataset.c = String(c);
        inp.autocomplete = "off";
        inp.addEventListener("input", () => {
          rows[r]![c] = inp.value;
          commit();
        });
        inp.addEventListener("keydown", (ev) => {
          if (ev.key === "Delete" || ev.key === "Backspace") {
            const b = selectedBounds();
            if (!(b.r0 === b.r1 && b.c0 === b.c1)) {
              ev.preventDefault();
              clearSelectionCells();
              return;
            }
          }
          let nr = r;
          let nc = c;
          const withShift = ev.shiftKey;
          if (ev.key === "ArrowUp") nr--;
          else if (ev.key === "ArrowDown") nr++;
          else if (ev.key === "ArrowLeft") nc--;
          else if (ev.key === "ArrowRight") nc++;
          else if (ev.key === "Enter") nr += withShift ? -1 : 1;
          else if (ev.key === "Tab") nc += withShift ? -1 : 1;
          else return;
          ev.preventDefault();
          nr = Math.max(0, Math.min(rows.length - 1, nr));
          nc = Math.max(0, Math.min(rows[0]!.length - 1, nc));
          focusR = nr;
          focusC = nc;
          if (withShift && ev.key.startsWith("Arrow")) {
            rangeEndR = nr;
            rangeEndC = nc;
          } else {
            resetRangeToFocus();
          }
          render();
          focusCellAt(nr, nc);
        });
        inp.addEventListener("copy", (ev) => {
          ev.preventDefault();
          ev.clipboardData?.setData("text/plain", selectionToTsv());
        });
        inp.addEventListener("cut", (ev) => {
          ev.preventDefault();
          ev.clipboardData?.setData("text/plain", selectionToTsv());
          clearSelectionCells();
        });
        inp.addEventListener("paste", (ev) => {
          const text = ev.clipboardData?.getData("text/plain");
          if (!text) return;
          ev.preventDefault();
          pasteGridAtSelection(text);
        });
        td.addEventListener("mousedown", (ev) => {
          if (ev.button !== 0) return;
          draggingSelection = true;
          focusR = r;
          focusC = c;
          rangeStartR = r;
          rangeStartC = c;
          rangeEndR = r;
          rangeEndC = c;
          const stopDragging = (): void => {
            draggingSelection = false;
            window.removeEventListener("mouseup", stopDragging);
          };
          window.addEventListener("mouseup", stopDragging);
        });
        td.addEventListener("mouseover", () => {
          if (!draggingSelection) return;
          rangeEndR = r;
          rangeEndC = c;
          focusR = r;
          focusC = c;
          render();
        });
        td.addEventListener("contextmenu", (ev) => {
          ev.preventDefault();
          focusR = r;
          focusC = c;
          rangeStartR = r;
          rangeStartC = c;
          rangeEndR = r;
          rangeEndC = c;
          render();
          openContextMenu(ev.clientX, ev.clientY, [
            { label: "Copy", action: () => copySelectionToClipboard() },
            {
              label: "Cut",
              action: async () => {
                await copySelectionToClipboard();
                clearSelectionCells();
              },
            },
            { label: "Paste", action: () => pasteFromClipboardToSelection() },
            { label: "Clear cells", action: () => clearSelectionCells() },
            { separator: true },
            { label: "Insert row above", action: () => doAction("row-ins-above") },
            { label: "Insert row below", action: () => doAction("row-ins-below") },
            { label: "Delete row", action: () => doAction("row-del") },
            { separator: true },
            { label: "Insert column left", action: () => doAction("col-ins-left") },
            { label: "Insert column right", action: () => doAction("col-ins-right") },
            { label: "Delete column", action: () => doAction("col-del") },
          ]);
        });
        td.appendChild(inp);
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    wrap.appendChild(table);
    tableHost.appendChild(wrap);
  };

  const doAction = (action: string): void => {
    ensureShape();
    const nr = rows.length;
    const nc = rows[0]!.length;
    switch (action) {
      case "row-end":
        rows.push(Array(nc).fill(""));
        break;
      case "row-ins-above": {
        const i = Math.min(focusR, nr - 1);
        rows.splice(i, 0, Array(nc).fill(""));
        break;
      }
      case "row-ins-below": {
        const i = Math.min(focusR + 1, nr);
        rows.splice(i, 0, Array(nc).fill(""));
        break;
      }
      case "row-del":
        if (nr <= 1) rows = [[""]];
        else rows.splice(Math.min(focusR, nr - 1), 1);
        break;
      case "col-end":
        for (const row of rows) row.push("");
        break;
      case "col-ins-left": {
        const j = Math.min(focusC, nc - 1);
        for (const row of rows) row.splice(j, 0, "");
        break;
      }
      case "col-ins-right": {
        const j = Math.min(focusC + 1, nc);
        for (const row of rows) row.splice(j, 0, "");
        break;
      }
      case "col-del":
        if (nc <= 1) rows = rows.map(() => [""]);
        else {
          const j = Math.min(focusC, nc - 1);
          for (const row of rows) row.splice(j, 1);
        }
        break;
      default:
        return;
    }
    ensureShape();
    commit();
    render();
  };

  buildToolbar(opsHost, doAction);
  resetRangeToFocus();

  return {
    destroy(): void {
      destroyed = true;
      closeContextMenu();
      opsHost.replaceChildren();
      tableHost.replaceChildren();
    },
    syncFromText(text: string): void {
      if (destroyed) return;
      rows = normalizeRows(parseCsv(text));
      render();
    },
  };
}
